import beTwMerge from '@/utils/beTwMerge';
import Button from '@/components/homepage/Button';
import DropdownButton from '@/components/homepage/DropdownButton/DropdownButton';
import { moreDescriptiveAriaLabel } from '@/utils/utils';
import { ColorKeys } from '@/styles/colors';
import RichText from '../../RichText/RichText';

type ActionType = 'primary' | 'secondary' | 'link';
type DropdownActionType = Exclude<ActionType, 'link'>;

export type Action = {
  label: string;
  type?: ActionType;
  color?: ColorKeys;
  onClick?: () => void;
  href?: string;
  target?: string;
  actions?: Action[];
};

export default function LargeCopySection({
  title = '',
  description,
  color = 'white',
  additionalClasses,
  actions = [],
  className = '',
  titleClassName = '',
}: {
  title?: string;
  description: string;
  color: ColorKeys | 'md:grey-20';
  additionalClasses?: string;
  actions?: null | Action[];
  className?: string;
  titleClassName?: string;
}) {
  const backgroundColor = {
    'sky-white': 'tw-bg-[#DDE8E9] tw-text-black',
    'light-green-light': 'tw-bg-color-light-green-light tw-text-white',
    'light-green': 'tw-bg-color-light-green tw-text-white',
    'dark-rose-light': 'tw-bg-color-dark-rose-light tw-text-color-black',
    'dark-rose': 'tw-bg-color-dark-rose tw-text-color-black',
    'dark-green': 'tw-bg-color-dark-green tw-text-white',
    white: 'tw-bg-white tw-text-color-black',
    'grey-20': 'tw-bg-color-grey-20',
    'md:grey-20': 'tw-bg-white md:tw-bg-color-grey-20',
  };
  const textColor = backgroundColor[color].includes('tw-text-white') ? 'tw-text-white' : 'tw-text-color-black';

  return (
    <section
      className={beTwMerge(
        'tw-px-8 lg:tw-px-0 tw-py-[80px] md:tw-mb-14 lg:tw-mb-20 tw-flex tw-flex-col tw-items-center',
        backgroundColor[color],
        textColor,
        additionalClasses
      )}
    >
      {!!title && (
        <h2
          className={beTwMerge(
            'tw-mb-6 lg:tw-mb-8 tw-uppercase tw-tracking-wide h2b tw-text-center',
            textColor,
            titleClassName
          )}
        >
          {title}
        </h2>
      )}
      <RichText
        className={beTwMerge(
          'lg:tw-max-w-[910px] tw-text-center tw-text-white [&_p]:tw-font-avenirmedium [&_p]:max-md:tw-text-base [&_p]:md:tw-text-base [&_p]:lg:tw-text-[32px] tw-font-light [&>h2]:tw-mb-0 [&>p]:!tw-leading-[1.35em]',
          textColor,
          className
        )}
        content={description}
      />
      {!!actions?.length && (
        <div className="tw-flex tw-flex-col md:tw-flex-row md:tw-justify-center md:tw-gap-x-2 tw-mt-8 tw-mb-4 tw-gap-y-2 tw-justify-center tw-items-center tw-w-full">
          {actions?.slice(0, 2).map(action => {
            if (action.actions) {
              const { actions: subActions } = action;
              return (
                <DropdownButton
                  key={action.label.split(' ').join('-')}
                  label={action.label}
                  options={subActions}
                  color={action.color}
                  type={action.type as DropdownActionType}
                  className={beTwMerge('tw-max-w-[768px]')}
                />
              );
            }
            const { href } = action;
            return (
              <Button
                key={action.label.split(' ').join('-')}
                className={beTwMerge('tw-max-w-[768px]')}
                ariaLabel={moreDescriptiveAriaLabel(title, action.label)}
                label={action.label}
                color={action.color}
                type={action.type}
                href={href}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}
